// Noyau — FEUILLES « REMPLIR UN TABLEAU » au format publié (plugin « tables », lot I8).
//
// Transcription du juge de l'ancien moteur (`packages/reasoner/src/kernel-tables.ts`, lots 6.21 à 6.23) :
// statistiques (série brute, effectifs, effectifs cumulés), proportionnalité, double entrée, tableau de valeurs
// (et de restes), tableau des fréquences. Le tableau de l'énoncé et celui de l'élève sont UN bout « ⊟{…} » ;
// chaque choix du QCM est une affirmation (`kernel.tclaims`), tranchée par ce que la feuille établit. Les
// vérités sont EXACTES (rationnels sur BigInt) ; une formule de tableau de valeurs se lit et se calcule avec le
// lecteur et le calcul exact du moteur v2 (`parse`, `evaluate`).
//
// Le juge de la feuille (judge.js) appelle `createTableSheet` à la création, `judgeTableLine` pour chaque ligne
// et range le nouvel état renvoyé dans la ligne gardée (l'état est immuable).
// ============================================================================

import { num, symbols } from './ast.js';
import { parse } from './syntax.js';
import { evaluate } from './equiv.js';

// ── Rationnels exacts sur BigInt ({ n, d }, d > 0, réduits) ─────────────────
const bgcd = (a, b) => {
  a = a < 0n ? -a : a;
  b = b < 0n ? -b : b;
  while (b) [a, b] = [b, a % b];
  return a;
};
function rat(n, d = 1n) {
  let nn = BigInt(n),
    dd = BigInt(d);
  if (dd === 0n) throw new RangeError('dénominateur nul');
  if (dd < 0n) {
    nn = -nn;
    dd = -dd;
  }
  const g = bgcd(nn, dd) || 1n;
  return { n: nn / g, d: dd / g };
}
const ZERO = rat(0);
const ONE = rat(1);
const add = (a, b) => rat(a.n * b.d + b.n * a.d, a.d * b.d);
const sub = (a, b) => rat(a.n * b.d - b.n * a.d, a.d * b.d);
const mul = (a, b) => rat(a.n * b.n, a.d * b.d);
const div = (a, b) => (b.n === 0n ? null : rat(a.n * b.d, a.d * b.n));
const cmp = (a, b) => {
  const x = a.n * b.d - b.n * a.d;
  return x === 0n ? 0 : x < 0n ? -1 : 1;
};
const eq = (a, b) => a.n === b.n && a.d === b.d;
const isInteger = (a) => a.d === 1n;
const DECIMAL_RE = /^([+\-−]?)(\d{1,40})(?:[.,](\d{1,40}))?$/;
/** « 1,414 » → 707/500 (exact) ; null pour toute autre écriture */
function parseDecimal(text) {
  const m = DECIMAL_RE.exec(text.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const r = rat(BigInt(m[2] + frac), 10n ** BigInt(frac.length));
  return m[1] === '-' || m[1] === '−' ? { n: -r.n, d: r.d } : r;
}
/** `approx` arrondit-il `value` à `places` décimales ? |value − approx| ≤ ½ × 10^−places, décidé exactement */
function isRoundingOf(value, approx, places) {
  if (!Number.isInteger(places) || places < 0 || places > 30) return false;
  const half = rat(1n, 2n * 10n ** BigInt(places));
  return cmp(value, sub(approx, half)) >= 0 && cmp(value, add(approx, half)) <= 0;
}
const fromRat = (r) => r;
/** nœud Num du moteur (nombres JS) → rationnel BigInt ; null si ce n'est pas un nombre exact */
const ratOfNum = (v) =>
  v && v.t === 'Num' && Number.isSafeInteger(v.p) && Number.isSafeInteger(v.q)
    ? rat(v.p, v.q)
    : null;
const numOfRat = (r) => num(Number(r.n), Number(r.d));

/* ------------------------------ le bout « tableau » ------------------------------ */
/** marque d'un tableau à remplir (statistiques, proportionnalité, double entrée) */
export const GRID_MARK = '⊟';
/** « ⊟{"h":[…],"r":[[étiquette,[cases]],…]} » */
export function gridText(g) {
  const o = {
    ...(g.head ? { h: g.head } : {}),
    r: g.rows.map((r) => [r.label, r.cells]),
  };
  return GRID_MARK + JSON.stringify(o);
}
export function isGridText(text) {
  return text.startsWith(GRID_MARK + '{');
}
const okText = (s, max) => typeof s === 'string' && s.length <= max && !/[\n\r]/.test(s);
/** relit `gridText` ; null si l'écriture ne va pas */
export function parseGrid(text) {
  if (!isGridText(text)) return null;
  let o;
  try {
    o = JSON.parse(text.slice(GRID_MARK.length));
  } catch {
    return null;
  }
  if (!o || typeof o !== 'object') return null;
  const { h, r } = o;
  if (h !== undefined && (!Array.isArray(h) || h.length > 13 || !h.every((x) => okText(x, 40))))
    return null;
  if (!Array.isArray(r) || r.length < 1 || r.length > 6) return null;
  const rows = [];
  for (const row of r) {
    if (!Array.isArray(row) || row.length !== 2) return null;
    const [label, cells] = row;
    if (!okText(label, 40) || !Array.isArray(cells) || cells.length < 1 || cells.length > 24)
      return null;
    if (!cells.every((c) => okText(c, 24))) return null;
    rows.push({ label, cells: cells });
  }
  if (h && rows.some((row) => row.cells.length !== h.length)) return null;
  return { ...(h ? { head: h } : {}), rows };
}
/* ------------------------------ nombres exacts ------------------------------ */
/** « 12 », « −3 », « 1,80 », « 1 050 », « 3/4 », « 3 ÷ 4 » → rationnel exact ; null sinon */
export function readQ(text) {
  const s = text.replace(/[\s\u00a0\u202f\u2009]/g, '').replace(/−/g, '-');
  if (!s) return null;
  const parts = s.split(/[/÷]/);
  if (parts.length > 2) return null;
  const n = parseDecimal(parts[0]);
  if (!n) return null;
  if (parts.length === 1) return n;
  const d = parseDecimal(parts[1]);
  return d ? div(n, d) : null;
}
/** écriture française : « 12 », « 8,5 », « −0,75 », sinon la fraction « 7/3 » */
export function qText(r) {
  let d = r.d;
  let k = 0;
  while (d % 2n === 0n) {
    d /= 2n;
    k++;
  }
  let k5 = 0;
  while (d % 5n === 0n) {
    d /= 5n;
    k5++;
  }
  const minus = r.n < 0n ? '−' : '';
  const n = r.n < 0n ? -r.n : r.n;
  if (d !== 1n) return `${minus}${n}/${r.d}`;
  const p = Math.max(k, k5);
  const scaled = (n * 10n ** BigInt(p)) / r.d;
  const s = scaled.toString().padStart(p + 1, '0');
  const int = s.slice(0, s.length - p);
  const frac = p ? s.slice(s.length - p).replace(/0+$/, '') : '';
  return minus + int + (frac ? ',' + frac : '');
}
const q = (n) => rat(BigInt(n));
/* ------------------------------ pas ------------------------------ */
export const TABLES_STEP_IDS = { fill: 'tables.fill@1', read: 'tables.read@1' };
const CARD = 'Tableau';
const FILL_RULE = {
  series: {
    id: TABLES_STEP_IDS.fill,
    card: CARD,
    label: 'Tableau des effectifs',
    hora: 3,
    why: 'Les valeurs rangées, l’effectif de chacune, puis les effectifs cumulés : le rang de chaque valeur se lit dans la dernière ligne.',
  },
  proportion: {
    id: TABLES_STEP_IDS.fill,
    card: CARD,
    label: 'Tableau de proportionnalité',
    hora: 3,
    why: 'Dans un tableau de proportionnalité, on passe d’une ligne à l’autre en multipliant par le même nombre : le coefficient (le quotient de chaque colonne).',
  },
  cross: {
    id: TABLES_STEP_IDS.fill,
    card: CARD,
    label: 'Tableau à double entrée',
    hora: 3,
    why: 'Dans un tableau à double entrée, les cases d’une ligne (ou d’une colonne) s’additionnent dans sa case « Total ».',
  },
  values: {
    id: TABLES_STEP_IDS.fill,
    card: CARD,
    label: 'Tableau de valeurs',
    hora: 3,
    why: 'Pour chaque colonne, on remplace la variable par sa valeur dans la formule de la ligne, puis on calcule.',
  },
  freq: {
    id: TABLES_STEP_IDS.fill,
    card: CARD,
    label: 'Tableau des fréquences',
    hora: 3,
    why: 'Fréquence = effectif ÷ effectif total (× 100 pour un pourcentage). Les fréquences se comparent, même quand les effectifs totaux ne sont pas les mêmes.',
  },
};
const RESIDUE_RULE = {
  id: TABLES_STEP_IDS.fill,
  card: CARD,
  label: 'Tableau de restes',
  hora: 3,
  why: 'Tout entier a un reste entre 0 et m − 1 : pour chacun, on calcule l’expression et son reste. Le tableau contient toutes les possibilités.',
};
const READ_RULE = {
  id: TABLES_STEP_IDS.read,
  card: CARD,
  label: 'Lire le tableau',
  hora: 1,
  why: 'La réponse se lit dans le tableau.',
};
export class TablesModelError extends Error {}
const SERIES_LABELS = ['Valeur', 'Effectif', 'Effectif cumulé'];
const isTotal = (s) => /^total$/i.test(s.trim());
function seriesFrom(form, g) {
  const nums = (cells) =>
    cells.map((c) => {
      const r = readQ(c);
      if (!r) throw new TablesModelError(`nombre illisible dans l’énoncé : « ${c} »`);
      return r;
    });
  const ints = (cells) =>
    nums(cells).map((r) => {
      if (!isInteger(r) || r.n < 0n) throw new TablesModelError('effectif attendu : entier ≥ 0');
      return r.n;
    });
  let values;
  let counts;
  if (form === 'raw') {
    if (g.rows.length !== 1) throw new TablesModelError('série brute : une seule ligne');
    const all = nums(g.rows[0].cells);
    values = [];
    counts = [];
    [...all]
      .sort((x, y) => cmp(x, y))
      .forEach((v) => {
        const last = values[values.length - 1];
        if (last && eq(last, v)) counts[counts.length - 1] += 1n;
        else {
          values.push(v);
          counts.push(1n);
        }
      });
  } else {
    if (g.rows.length !== 2) throw new TablesModelError('valeurs et effectifs : deux lignes');
    values = nums(g.rows[0].cells);
    for (let i = 1; i < values.length; i++)
      if (cmp(values[i - 1], values[i]) >= 0)
        throw new TablesModelError('valeurs de l’énoncé : rangées, chacune une fois');
    const second = ints(g.rows[1].cells);
    if (second.length !== values.length) throw new TablesModelError('une case par valeur');
    if (form === 'counts') counts = second;
    else {
      counts = second.map((c, i) => c - (i ? second[i - 1] : 0n));
      if (counts.some((c) => c <= 0n))
        throw new TablesModelError('effectifs cumulés : croissants strictement');
    }
  }
  if (counts.some((c) => c <= 0n)) throw new TablesModelError('effectif nul');
  const ecc = [];
  counts.forEach((c, i) => ecc.push(c + (i ? ecc[i - 1] : 0n)));
  return { kind: 'series', form, values, counts, ecc, N: ecc[ecc.length - 1] };
}
function propFrom(g, coef) {
  if (g.rows.length !== 2) throw new TablesModelError('proportionnalité : deux lignes');
  const read = (c) => {
    if (c.trim() === '?') return null;
    const r = readQ(c);
    if (!r) throw new TablesModelError(`nombre illisible dans l’énoncé : « ${c} »`);
    return r;
  };
  const givenA = g.rows[0].cells.map(read);
  const givenB = g.rows[1].cells.map(read);
  if (givenA.length !== givenB.length || givenA.length < 2)
    throw new TablesModelError('deux colonnes au moins, autant de cases par ligne');
  const full = [];
  givenA.forEach((x, i) => {
    if (x && givenB[i]) full.push(i);
    if (!x && !givenB[i]) throw new TablesModelError('une colonne sans aucune donnée');
    if (x && eq(x, ZERO)) throw new TablesModelError('une valeur nulle dans la 1re ligne');
  });
  if (!full.length) throw new TablesModelError('au moins une colonne complète');
  const k0 = div(givenB[full[0]], givenA[full[0]]);
  const proportional = full.every((i) => eq(div(givenB[i], givenA[i]), k0));
  const missing = givenA.some((x, i) => !x || !givenB[i]);
  if (missing && !proportional)
    throw new TablesModelError('une case à trouver dans un tableau qui n’est pas proportionnel');
  if (missing && eq(k0, ZERO)) throw new TablesModelError('coefficient nul');
  const a = givenA.map((x, i) => x ?? div(givenB[i], k0));
  const b = givenB.map((y, i) => y ?? mul(givenA[i], k0));
  return {
    kind: 'proportion',
    labels: [g.rows[0].label, g.rows[1].label],
    a,
    b,
    givenA,
    givenB,
    proportional,
    k: proportional ? k0 : null,
    coef,
  };
}
/** double entrée : la dernière ligne et la dernière colonne sont les totaux ; « ? » à trouver */
function crossFrom(g) {
  const head = g.head;
  if (!head || head.length < 3 || !isTotal(head[head.length - 1]))
    throw new TablesModelError('double entrée : des en-têtes, la dernière colonne « Total »');
  if (g.rows.length < 3 || !isTotal(g.rows[g.rows.length - 1].label))
    throw new TablesModelError('double entrée : la dernière ligne « Total »');
  const given = g.rows.map((r) =>
    r.cells.map((c) => {
      if (c.trim() === '?') return null;
      const v = readQ(c);
      if (!v) throw new TablesModelError(`nombre illisible dans l’énoncé : « ${c} »`);
      return v;
    }),
  );
  const R = given.length;
  const C = head.length;
  const full = given.map((r) => [...r]);
  // une ligne (ou une colonne) avec une seule inconnue : elle se déduit ; on recommence
  for (let pass = 0; pass < R * C; pass++) {
    let moved = false;
    const lines = [];
    for (let i = 0; i < R; i++) lines.push(Array.from({ length: C }, (_, j) => [i, j]));
    for (let j = 0; j < C; j++) lines.push(Array.from({ length: R }, (_, i) => [i, j]));
    for (const line of lines) {
      const unknown = line.filter(([i, j]) => !full[i][j]);
      if (unknown.length !== 1) continue;
      const [ui, uj] = unknown[0];
      const tot = line[line.length - 1];
      const parts = line.slice(0, -1);
      let v;
      if (ui === tot[0] && uj === tot[1]) v = parts.reduce((s, [i, j]) => add(s, full[i][j]), ZERO);
      else {
        v = full[tot[0]][tot[1]];
        for (const [i, j] of parts) if (i !== ui || j !== uj) v = sub(v, full[i][j]);
      }
      full[ui][uj] = v;
      moved = true;
    }
    if (!moved) break;
  }
  if (full.some((r) => r.some((c) => !c)))
    throw new TablesModelError('double entrée : des cases ne se déduisent pas');
  // les totaux tiennent
  const F = full;
  for (let i = 0; i < R; i++) {
    const s = F[i].slice(0, -1).reduce((x, y) => add(x, y), ZERO);
    if (!eq(s, F[i][C - 1])) throw new TablesModelError('double entrée : un total faux');
  }
  for (let j = 0; j < C; j++) {
    const s = F.slice(0, -1).reduce((x, r) => add(x, r[j]), ZERO);
    if (!eq(s, F[R - 1][j])) throw new TablesModelError('double entrée : un total faux');
  }
  return { kind: 'cross', head, labels: g.rows.map((r) => r.label), given, full: F };
}
/** la valeur exacte d'une formule en x (réduite modulo m) ; null : pas calculable */
function valueAt(term, variable, x, mod, table) {
  const v = ratOfNum(evaluate(term, { [variable]: numOfRat(x) }, table));
  if (!v) return null;
  if (mod === null) return v;
  if (!isInteger(v)) return null;
  const r = ((v.n % mod) + mod) % mod;
  return rat(r);
}
function valuesFrom(g, exprs, modN, table) {
  if (g.head) throw new TablesModelError('tableau de valeurs : pas d’en-têtes de colonnes');
  if (g.rows.length < 2)
    throw new TablesModelError('tableau de valeurs : la variable, puis une ligne au moins');
  const variable = g.rows[0].label.trim();
  if (!/^[a-zA-Z]$/.test(variable))
    throw new TablesModelError('la 1re ligne porte le nom de la variable (« x », « n »)');
  const mod = modN === undefined ? null : BigInt(modN);
  const xs = g.rows[0].cells.map((c) => {
    const r = readQ(c);
    if (!r) throw new TablesModelError(`valeur de ${variable} illisible : « ${c} »`);
    if (mod !== null && !isInteger(r))
      throw new TablesModelError('tableau de restes : des entiers');
    return r;
  });
  if (xs.length < 2) throw new TablesModelError('deux colonnes au moins');
  xs.forEach((x, i) => {
    if (xs.findIndex((y) => eq(x, y)) !== i)
      throw new TablesModelError(`${variable} : chaque valeur une seule fois`);
  });
  if (exprs.length !== g.rows.length - 1)
    throw new TablesModelError('`table.exprs` : une formule (ou null) par ligne après la 1re');
  let toFill = 0;
  const rows = g.rows.slice(1).map((r, i) => {
    const expr = exprs[i] ?? null;
    if (r.cells.length !== xs.length)
      throw new TablesModelError('une case par valeur de la variable');
    const given = r.cells.map((c) => {
      if (c.trim() === '?') return null;
      const v = readQ(c);
      if (!v) throw new TablesModelError(`nombre illisible dans l’énoncé : « ${c} »`);
      return v;
    });
    toFill += given.filter((c) => c === null).length;
    if (expr === null) {
      if (given.some((c) => c === null))
        throw new TablesModelError(
          `ligne « ${r.label} » sans formule : toutes ses cases sont données`,
        );
      return { label: r.label, expr: null, term: null, given, full: given };
    }
    let term;
    try {
      term = parse(expr, table);
    } catch {
      throw new TablesModelError(`formule illisible : « ${expr} »`);
    }
    const extra = [...symbols(term)].filter((n) => n !== variable);
    if (extra.length)
      throw new TablesModelError(`formule « ${expr} » : la seule lettre est ${variable}`);
    const full = xs.map((x) => {
      const v = valueAt(term, variable, x, mod, table);
      if (!v) throw new TablesModelError(`« ${expr} » ne se calcule pas exactement en ${qText(x)}`);
      return v;
    });
    given.forEach((gv, j) => {
      if (gv && !eq(gv, full[j]))
        throw new TablesModelError(
          `ligne « ${r.label} » : la case donnée sous ${qText(xs[j])} est fausse`,
        );
    });
    return { label: r.label, expr, term, given, full };
  });
  if (!toFill) throw new TablesModelError('tableau de valeurs : au moins une case à remplir');
  return { kind: 'values', variable, xs, rows, mod, table };
}
const HUNDRED = q(100);
const sumOf = (xs) => xs.reduce((a, b) => add(a, b), ZERO);
/** tableau des fréquences : les cases « ? » se déduisent (f = n ÷ N, totaux additionnés) */
function freqFrom(g, percent) {
  const head = g.head;
  if (!head?.length)
    throw new TablesModelError('fréquences : des en-têtes de colonnes (une par catégorie)');
  const R = g.rows.length;
  if (R !== 2 && R !== 3)
    throw new TablesModelError(
      'fréquences : la ligne des effectifs, (celle des effectifs totaux,) celle des fréquences',
    );
  const C = head.length;
  const totalCol = isTotal(head[C - 1]) ? C - 1 : -1;
  if (totalCol === 0) throw new TablesModelError('fréquences : une catégorie avant « Total »');
  const group = R === 3;
  const unit = percent ? HUNDRED : ONE;
  const given = g.rows.map((r) => {
    if (r.cells.length !== C) throw new TablesModelError('une case par colonne');
    return r.cells.map((c) => {
      if (c.trim() === '?') return null;
      const v = readQ(c);
      if (!v) throw new TablesModelError(`nombre illisible dans l’énoncé : « ${c} »`);
      return v;
    });
  });
  const toFill = given.flat().filter((c) => c === null).length;
  if (!toFill) throw new TablesModelError('fréquences : au moins une case à remplir');
  const cats = head.map((_, j) => j).filter((j) => j !== totalCol);
  const n = [...given[0]];
  const N = group ? [...given[1]] : head.map(() => null);
  const f = given[R - 1].map((v) => (v ? div(v, unit) : null));
  let Ns = null;
  for (let pass = 0; pass < 4 * (C + 2); pass++) {
    let moved = false;
    const set = (arr, j, v) => {
      if (!v || arr[j]) return;
      arr[j] = v;
      moved = true;
    };
    if (totalCol >= 0)
      for (const arr of group ? [n, N] : [n]) {
        const open = [...cats, totalCol].filter((j) => !arr[j]);
        if (open.length !== 1) continue;
        const u = open[0];
        if (u === totalCol) set(arr, u, sumOf(cats.map((j) => arr[j])));
        else set(arr, u, sub(arr[totalCol], sumOf(cats.filter((j) => j !== u).map((j) => arr[j]))));
      }
    if (group)
      head.forEach((_, j) => {
        const [a, b, c] = [n[j], N[j], f[j]];
        if (a && b && !c) set(f, j, div(a, b));
        if (a && c && !b && !eq(c, ZERO)) set(N, j, div(a, c));
        if (b && c && !a) set(n, j, mul(b, c));
      });
    else {
      if (!Ns) {
        if (totalCol >= 0 && n[totalCol]) Ns = n[totalCol];
        else if (cats.every((j) => n[j])) Ns = sumOf(cats.map((j) => n[j]));
        else {
          const j = cats.find((k) => n[k] && f[k] && !eq(f[k], ZERO));
          if (j !== undefined) Ns = div(n[j], f[j]);
        }
        if (Ns) moved = true;
      }
      if (Ns) {
        if (totalCol >= 0) {
          set(n, totalCol, Ns);
          set(f, totalCol, ONE);
        }
        for (const j of cats) {
          if (n[j] && !f[j]) set(f, j, div(n[j], Ns));
          if (f[j] && !n[j]) set(n, j, mul(f[j], Ns));
        }
      }
    }
    if (!moved) break;
  }
  if ([...n, ...f].some((x) => !x) || (group ? N.some((x) => !x) : !Ns))
    throw new TablesModelError('fréquences : des cases ne se déduisent pas');
  const nn = n;
  const ff = f;
  const NN = group ? N : head.map(() => Ns);
  // tout tient : effectifs ≥ 0, totaux > 0, fréquences entre 0 et 1, f = n ÷ N, totaux
  head.forEach((h, j) => {
    if (cmp(nn[j], ZERO) < 0 || cmp(NN[j], ZERO) <= 0)
      throw new TablesModelError(`colonne « ${h} » : effectif négatif ou effectif total nul`);
    if (cmp(ff[j], ZERO) < 0 || cmp(ff[j], ONE) > 0)
      throw new TablesModelError(`colonne « ${h} » : une fréquence entre 0 et 1`);
    if (!eq(mul(ff[j], NN[j]), nn[j]))
      throw new TablesModelError(`colonne « ${h} » : fréquence ≠ effectif ÷ effectif total`);
  });
  if (totalCol >= 0) {
    if (!eq(sumOf(cats.map((j) => nn[j])), nn[totalCol]))
      throw new TablesModelError('fréquences : le total des effectifs est faux');
    if (group && !eq(sumOf(cats.map((j) => NN[j])), NN[totalCol]))
      throw new TablesModelError('fréquences : le total des effectifs totaux est faux');
  } else if (!group && !eq(sumOf(cats.map((j) => nn[j])), Ns))
    throw new TablesModelError('fréquences : l’effectif total n’est pas la somme des effectifs');
  const full = [nn, ...(group ? [NN] : []), ff.map((x) => mul(x, unit))];
  given.forEach((row, i) =>
    row.forEach((v, j) => {
      if (v && !eq(v, full[i][j]))
        throw new TablesModelError(`colonne « ${head[j]} » : une case donnée ne tient pas`);
    }),
  );
  return {
    kind: 'freq',
    head,
    labels: g.rows.map((r) => r.label),
    totalCol,
    group,
    percent,
    given,
    full,
    n: nn,
    N: NN,
    f: ff,
  };
}
function tableData(model, table) {
  const spec = model.kernel;
  const t = spec.table;
  if (!t) throw new TablesModelError('`table` manquant');
  const g = parseGrid(spec.start);
  if (!g) throw new TablesModelError('`start` : le tableau de l’énoncé « ⊟{…} »');
  if (t.kind === 'series') return seriesFrom(t.form ?? 'raw', g);
  if (t.kind === 'proportion') return propFrom(g, !!t.coef);
  if (t.kind === 'values') return valuesFrom(g, t.exprs ?? [], t.mod, table);
  if (t.kind === 'freq') return freqFrom(g, !!t.percent);
  return crossFrom(g);
}
/* ------------------------------ statistiques ------------------------------ */
/** la valeur de rang r (1 ≤ r ≤ N) de la série rangée */
function atRank(d, r) {
  const i = d.ecc.findIndex((e) => e >= r);
  return d.values[i];
}
const ceilDiv = (a, b) => (a + b - 1n) / b;
function median(d) {
  if (d.N % 2n === 1n) return atRank(d, (d.N + 1n) / 2n);
  return div(add(atRank(d, d.N / 2n), atRank(d, d.N / 2n + 1n)), q(2));
}
const quartile1 = (d) => atRank(d, ceilDiv(d.N, 4n));
const quartile3 = (d) => atRank(d, ceilDiv(3n * d.N, 4n));
/** les grandeurs qu'on peut lire, et leur valeur exacte */
const READABLE = {
  series: ['N', 'Me', 'Q₁', 'Q₃'],
  proportion: ['k'],
  cross: [],
  values: [],
  freq: [],
};
const NAME_ALIASES = {
  Q1: 'Q₁',
  Q3: 'Q₃',
  Q_1: 'Q₁',
  Q_3: 'Q₃',
  M: 'Me',
};
const normName = (n) => NAME_ALIASES[n] ?? n;
/** la valeur exacte d'une grandeur (« Me », « n(12) », « ECC(12) », « k », « y(10) », « T(Foot) ») */
function statValue(d, key) {
  if (d.kind === 'freq') {
    if (key === 'N') return d.totalCol >= 0 ? d.N[d.totalCol] : d.group ? undefined : d.N[0];
    const fm = /^(f|n|N)\((.+)\)$/.exec(key);
    const j = fm ? d.head.findIndex((h) => sameLabel(h, fm[2])) : -1;
    if (!fm || j < 0) return undefined;
    if (fm[1] === 'f') return mul(d.f[j], d.percent ? HUNDRED : ONE);
    return fm[1] === 'n' ? d.n[j] : d.N[j];
  }
  const m = /^(n|ECC|y|T)\((.+)\)$/.exec(key);
  if (d.kind === 'series') {
    if (key === 'N') return q(d.N);
    if (key === 'Me') return median(d);
    if (key === 'Q₁') return quartile1(d);
    if (key === 'Q₃') return quartile3(d);
    if (m && (m[1] === 'n' || m[1] === 'ECC')) {
      const v = readQ(m[2]);
      if (!v) return undefined;
      const i = d.values.findIndex((x) => eq(x, v));
      if (m[1] === 'n') return q(i < 0 ? 0n : d.counts[i]);
      // effectif cumulé « jusqu'à v » : les valeurs ≤ v
      let s = 0n;
      d.values.forEach((x, j) => {
        if (cmp(x, v) <= 0) s += d.counts[j];
      });
      return q(s);
    }
    return undefined;
  }
  if (d.kind === 'proportion') {
    if (key === 'k') return d.k;
    if (m && m[1] === 'y') {
      const v = readQ(m[2]);
      const i = v ? d.a.findIndex((x) => eq(x, v)) : -1;
      return i < 0 ? undefined : d.b[i];
    }
    return undefined;
  }
  if (d.kind === 'values') return undefined;
  if (key === 'T') return d.full[d.full.length - 1][d.head.length - 1];
  if (m && m[1] === 'T') {
    const label = m[2].trim();
    const i = d.labels.findIndex((l) => l.trim() === label);
    if (i >= 0) return d.full[i][d.head.length - 1];
    const j = d.head.findIndex((l) => l.trim() === label);
    if (j >= 0) return d.full[d.full.length - 1][j];
  }
  return undefined;
}
function expectedGrid(d, start) {
  if (d.kind === 'series') {
    const vt = d.values.map(qText);
    const giv = (i, j) => {
      if (d.form === 'raw') return null;
      if (i === 0) return start.rows[0].cells[j];
      if (i === 1 && d.form === 'counts') return start.rows[1].cells[j];
      if (i === 2 && d.form === 'cumulative') return start.rows[1].cells[j];
      return null;
    };
    const roles = ['value', 'count', 'ecc'];
    const wants = [d.values, d.counts.map(q), d.ecc.map(q)];
    return {
      kind: 'series',
      rows: SERIES_LABELS.map((label, i) => ({
        label,
        role: roles[i],
        cells: vt.map((_, j) => ({ given: giv(i, j), want: wants[i][j] })),
      })),
    };
  }
  if (d.kind === 'proportion') {
    const rows = [
      {
        label: d.labels[0],
        role: 'a',
        cells: d.a.map((w, j) => ({
          given: d.givenA[j] ? start.rows[0].cells[j] : null,
          want: w,
        })),
      },
      {
        label: d.labels[1],
        role: 'b',
        cells: d.b.map((w, j) => ({
          given: d.givenB[j] ? start.rows[1].cells[j] : null,
          want: w,
        })),
      },
    ];
    if (d.coef)
      rows.push({
        label: `${d.labels[1]} ÷ ${d.labels[0]}`,
        role: 'quotient',
        cells: d.a.map((x, j) => ({ given: null, want: div(d.b[j], x) })),
      });
    return { kind: 'proportion', rows };
  }
  if (d.kind === 'values') {
    const xs = d.xs.map(qText);
    return {
      kind: 'values',
      rows: [
        {
          label: start.rows[0].label,
          role: 'var',
          cells: d.xs.map((x, j) => ({ given: start.rows[0].cells[j], want: x })),
        },
        ...d.rows.map((r, i) => ({
          label: r.label,
          role: 'fn',
          cells: r.full.map((w, j) => ({
            given: r.given[j] ? start.rows[i + 1].cells[j] : null,
            want: w,
          })),
          ...(r.expr
            ? {
                fn: {
                  variable: d.variable,
                  xs,
                  expr: r.expr,
                  mod: d.mod === null ? null : d.mod.toString(),
                },
              }
            : {}),
        })),
      ],
    };
  }
  if (d.kind === 'freq') {
    const fq = {
      head: d.head,
      totalCol: d.totalCol,
      group: d.group,
      percent: d.percent,
      n: d.n,
      N: d.N,
    };
    const roles = d.group ? ['fcount', 'ftotal', 'freq'] : ['fcount', 'freq'];
    return {
      kind: 'freq',
      head: d.head,
      rows: d.labels.map((label, i) => ({
        label,
        role: roles[i],
        fq,
        cells: d.full[i].map((w, j) => ({
          given: d.given[i][j] ? start.rows[i].cells[j] : null,
          want: w,
        })),
      })),
    };
  }
  return {
    kind: 'cross',
    head: d.head,
    rows: d.labels.map((label, i) => ({
      label,
      role: 'cross',
      cells: d.full[i].map((w, j) => ({
        given: d.given[i][j] ? start.rows[i].cells[j] : null,
        want: w,
      })),
    })),
  };
}
const sameLabel = (a, b) =>
  a.replace(/\s+/g, ' ').trim().toLowerCase() === b.replace(/\s+/g, ' ').trim().toLowerCase();
/** le tableau de l'élève, case par case ; null s'il est juste */
export function checkGrid(exp, t) {
  const shape = {
    title: 'Ce tableau ne va pas avec l’énoncé',
    message: 'Ouvre le tableau depuis la palette : il a les bonnes lignes et les bonnes colonnes.',
  };
  if (t.rows.length !== exp.rows.length) return shape;
  if (exp.head && (!t.head || t.head.length !== exp.head.length)) return shape;
  for (let i = 0; i < exp.rows.length; i++) {
    const er = exp.rows[i];
    const tr = t.rows[i];
    if (!sameLabel(er.label, tr.label) || tr.cells.length !== er.cells.length) return shape;
  }
  for (let i = 0; i < exp.rows.length; i++) {
    const er = exp.rows[i];
    const tr = t.rows[i];
    for (let j = 0; j < er.cells.length; j++) {
      const ec = er.cells[j];
      const have = tr.cells[j].trim();
      const at = { row: i, col: j };
      if (ec.given !== null) {
        const g = readQ(have);
        if (!g || !eq(g, ec.want))
          return {
            title: 'C’est une donnée de l’énoncé',
            message: 'Recopie cette case telle qu’elle est dans l’énoncé.',
            ...at,
          };
        continue;
      }
      if (!have)
        return {
          title: 'Il manque une case',
          message: emptyMessage(exp, er, j),
          ...at,
        };
      const got = readQ(have);
      if (!got)
        return {
          title: 'Case illisible',
          message: 'Écris un nombre dans chaque case : « 12 », « 2,5 » ou une fraction « 3/4 ».',
          ...at,
        };
      if (eq(got, ec.want)) continue;
      return { ...wrongCell(exp, er, j, got), ...at };
    }
  }
  return null;
}
function emptyMessage(exp, er, j) {
  if (er.fq) {
    const fq = er.fq;
    const h = fq.head[j];
    const tot = j === fq.totalCol;
    if (er.role === 'fcount')
      return tot
        ? 'Dans la colonne « Total » : additionne les effectifs.'
        : `Sous « ${h} » : effectif = fréquence × effectif total.`;
    if (er.role === 'ftotal')
      return tot
        ? 'Dans la colonne « Total » : additionne les effectifs totaux.'
        : `Sous « ${h} » : effectif total = effectif ÷ fréquence.`;
    if (tot && !fq.group)
      return fq.percent
        ? 'Toutes les fréquences ensemble font 100 %.'
        : 'Toutes les fréquences ensemble font 1.';
    return `Sous « ${h} » : fréquence = effectif ÷ effectif total${fq.percent ? ', puis × 100 pour un pourcentage' : ''}.`;
  }
  if (er.role === 'value')
    return 'Écris chaque valeur de la série une seule fois, de la plus petite à la plus grande.';
  if (er.role === 'count')
    return `Écris sous chaque valeur son effectif : combien de fois elle apparaît dans la série.`;
  if (er.role === 'ecc')
    return 'Sous chaque valeur, l’effectif cumulé : les effectifs jusqu’à cette valeur, additionnés.';
  if (er.role === 'quotient') return 'Dans chaque colonne, divise la 2e ligne par la 1re.';
  if (er.role === 'cross')
    return `Chaque case se déduit : une ligne (ou une colonne) s’additionne dans sa case « Total ».`;
  if (er.role === 'fn' && er.fn) {
    const x = er.fn.xs[j];
    return er.fn.mod
      ? `Sous ${er.fn.variable} = ${x} : calcule ${er.fn.expr} pour ${er.fn.variable} = ${x}, puis son reste dans la division par ${er.fn.mod}.`
      : `Sous ${er.fn.variable} = ${x} : remplace ${er.fn.variable} par ${x} dans ${er.fn.expr}, puis calcule.`;
  }
  void exp;
  return 'Le tableau est proportionnel : trouve la case avec le coefficient (ou un produit en croix).';
}
function wrongFreqCell(er, j, got) {
  const fq = er.fq;
  const h = fq.head[j];
  const tot = j === fq.totalCol;
  const want = er.cells[j].want;
  if (er.role === 'fcount')
    return {
      title: 'Pas le bon effectif',
      message: tot
        ? 'Dans la colonne « Total », additionne les effectifs.'
        : `Sous « ${h} » : effectif = fréquence × effectif total.`,
    };
  if (er.role === 'ftotal')
    return {
      title: 'Pas le bon effectif total',
      message: tot
        ? 'Dans la colonne « Total », additionne les effectifs totaux.'
        : `Sous « ${h} » : effectif total = effectif ÷ fréquence.`,
    };
  if (fq.percent && eq(mul(got, HUNDRED), want))
    return {
      title: 'En pourcentage',
      message: 'Cette ligne est en pourcentage : multiplie ta fréquence par 100.',
    };
  if (!fq.percent && eq(got, mul(want, HUNDRED)))
    return {
      title: 'Une fréquence est entre 0 et 1',
      message: 'Ici, pas de pourcentage : divise par 100 (25 % → 0,25).',
    };
  const n = fq.n[j];
  if (!eq(n, ZERO) && eq(got, mul(div(fq.N[j], n), fq.percent ? HUNDRED : ONE)))
    return {
      title: 'À l’envers',
      message: 'Fréquence = effectif ÷ effectif total, pas l’inverse.',
    };
  if (cmp(got, fq.percent ? HUNDRED : ONE) > 0)
    return {
      title: fq.percent ? 'Pas plus de 100 %' : 'Une fréquence est entre 0 et 1',
      message: 'Divise l’effectif par l’effectif total : la fréquence ne dépasse jamais le tout.',
    };
  const close =
    !eq(want, ZERO) && Math.abs(ratNum(got) - ratNum(want)) < 0.01 * Math.abs(ratNum(want));
  if (close)
    return {
      title: 'Une valeur approchée',
      message: 'La fréquence doit être exacte : écris-la en fraction, effectif / effectif total.',
    };
  if (tot && !fq.group)
    return {
      title: 'Pas la bonne fréquence',
      message: fq.percent
        ? 'Toutes les fréquences ensemble font 100 %.'
        : 'Toutes les fréquences ensemble font 1.',
    };
  return {
    title: 'Pas la bonne fréquence',
    message: `Sous « ${h} » : divise l’effectif par l’effectif total${fq.percent ? ', puis multiplie par 100' : ''}.`,
  };
}
function wrongCell(exp, er, j, got) {
  if (er.fq) return wrongFreqCell(er, j, got);
  if (er.role === 'value') {
    const vals = er.cells.map((c) => c.want);
    const inSeries = vals.some((v) => eq(v, got));
    if (inSeries)
      return {
        title: 'Dans l’ordre croissant',
        message: 'Range les valeurs de la plus petite à la plus grande, chacune une seule fois.',
      };
    return {
      title: 'Une valeur qui n’est pas dans la série',
      message: `${qText(got)} n’est pas une valeur de la série : recopie les valeurs de l’énoncé, rangées.`,
    };
  }
  if (er.role === 'fn' && er.fn) {
    const x = er.fn.xs[j];
    if (er.fn.mod) {
      const m = BigInt(er.fn.mod);
      if (!isInteger(got) || got.n < 0n || got.n >= m)
        return {
          title: 'Pas un reste',
          message: `Un reste de la division par ${er.fn.mod} est un entier entre 0 et ${(m - 1n).toString()}.`,
        };
      return {
        title: 'Pas le bon reste',
        message: `Calcule ${er.fn.expr} pour ${er.fn.variable} = ${x}, puis son reste dans la division par ${er.fn.mod}.`,
      };
    }
    return {
      title: 'Pas la bonne valeur',
      message: `Remplace ${er.fn.variable} par ${x} dans ${er.fn.expr}, puis calcule.`,
    };
  }
  const v = exp.rows[0]?.cells[j] ? qText(exp.rows[0].cells[j].want) : '';
  if (er.role === 'count')
    return {
      title: 'Pas le bon effectif',
      message: `Compte combien de fois ${v} apparaît dans la série.`,
    };
  if (er.role === 'ecc')
    return {
      title: 'Pas le bon effectif cumulé',
      message:
        j === 0
          ? 'Sous la plus petite valeur, l’effectif cumulé est son effectif.'
          : `Sous ${v} : l’effectif cumulé d’avant, plus l’effectif de ${v}.`,
    };
  if (er.role === 'quotient') {
    const approx =
      got.d !== 1n &&
      Math.abs(Number(got.n) / Number(got.d) - ratNum(er.cells[j].want)) <
        0.01 * Math.abs(ratNum(er.cells[j].want));
    return approx
      ? {
          title: 'Une valeur approchée',
          message: 'Le quotient doit être exact : écris-le en fraction (« 14/6 »).',
        }
      : {
          title: 'Pas le bon quotient',
          message: 'Dans cette colonne, divise le nombre de la 2e ligne par celui de la 1re.',
        };
  }
  if (er.role === 'cross')
    return {
      title: 'Pas le bon nombre',
      message:
        'Sur une ligne (ou une colonne), les cases s’additionnent dans la case « Total » : additionne, ou retranche du total.',
    };
  return {
    title: 'Pas le bon nombre',
    message:
      'Le tableau est proportionnel : multiplie par le coefficient (ou fais un produit en croix).',
  };
}
const ratNum = (r) => Number(r.n) / Number(r.d);
/** les choix tranchés par la ligne : ceux qui ne l'étaient pas avant */
function newlyDecided(before, after) {
  return after.flatMap((d, index) =>
    d !== null && before[index] === null ? [{ index, truth: d }] : [],
  );
}
function claimTruth(d, c) {
  const stats = Object.entries(c.stats ?? {});
  const statsOk = () =>
    stats.every(([k, v]) => {
      const want = statValue(d, normName(k));
      const got = readQ(v);
      if (want === undefined) throw new TablesModelError(`grandeur inconnue : « ${k} »`);
      if (!got) throw new TablesModelError(`valeur illisible : « ${v} »`);
      if (want === null) return false;
      return c.places === undefined ? eq(want, got) : isRoundingOf(fromRat(want), got, c.places);
    });
  switch (c.kind) {
    case 'value':
      if (!stats.length)
        throw new TablesModelError('une affirmation « value » donne des grandeurs');
      return statsOk();
    case 'proportional':
      if (d.kind !== 'proportion')
        throw new TablesModelError('« proportional » : proportionnalité');
      return d.proportional && statsOk();
    case 'not_proportional':
      if (d.kind !== 'proportion')
        throw new TablesModelError('« not_proportional » : proportionnalité');
      return !d.proportional;
    case 'other':
      return null;
    default:
      return d.kind === 'freq' ? freqClaimTruth(d, c) : valuesClaimTruth(d, c);
  }
}
/* ------------------------------ tableau des fréquences : affirmations ------------------------------ */
function freqClaimTruth(d, c) {
  const cols = (c.cols ?? []).map((l) => {
    const j = d.head.findIndex((h) => sameLabel(h, l));
    if (j < 0 || j === d.totalCol)
      throw new TablesModelError(`affirmation « ${c.text} » : pas de catégorie « ${l} »`);
    return j;
  });
  if (c.kind === 'largest') {
    if (cols.length !== 1) throw new TablesModelError('« largest » : une colonne (`cols`)');
    const j = cols[0];
    return d.head.every((_, k) => k === j || k === d.totalCol || cmp(d.f[j], d.f[k]) > 0);
  }
  if (c.kind === 'same_freq') {
    if (cols.length < 2) throw new TablesModelError('« same_freq » : deux colonnes au moins');
    return cols.every((j) => eq(d.f[j], d.f[cols[0]]));
  }
  throw new TablesModelError(`« ${c.kind} » : pas sur un tableau des fréquences`);
}
/* ------------------------------ tableau de valeurs : affirmations ------------------------------ */
const readList = (vs, what) => {
  if (!vs?.length) throw new TablesModelError(`${what} : la liste \`values\` manque`);
  return vs.map((v) => {
    const r = readQ(v);
    if (!r) throw new TablesModelError(`valeur illisible : « ${v} »`);
    return r;
  });
};
/** la ligne calculée d'une affirmation (`row`, 1 par défaut) */
function claimRow(d, c) {
  const i = c.row ?? 1;
  const r = d.rows[i - 1];
  if (!r) throw new TablesModelError(`affirmation « ${c.text} » : pas de ligne ${i}`);
  return r;
}
/** vérité exacte d'une affirmation sur un tableau de valeurs */
function valuesClaimTruth(d, c) {
  if (d.kind !== 'values')
    throw new TablesModelError(`« ${c.kind} » : un tableau de valeurs (\`table.kind = 'values'\`)`);
  if (c.kind === 'same_rows') {
    const [a, b] = c.rows ?? [];
    const ra = a ? d.rows[a - 1] : undefined;
    const rb = b ? d.rows[b - 1] : undefined;
    if (!ra || !rb || a === b) throw new TablesModelError('« same_rows » : deux lignes (`rows`)');
    return ra.full.every((v, j) => eq(v, rb.full[j]));
  }
  const row = claimRow(d, c);
  if (c.kind === 'pairs') {
    if (!c.pairs?.length) throw new TablesModelError('« pairs » : la liste `pairs` manque');
    if (!row.term) throw new TablesModelError('« pairs » : sur une ligne avec une formule');
    return c.pairs.every(([xs, ys]) => {
      const x = readQ(xs);
      const y = readQ(ys);
      if (!x || !y) throw new TablesModelError(`couple illisible : (${xs} ; ${ys})`);
      const v = valueAt(row.term, d.variable, x, d.mod, d.table);
      return !!v && eq(v, y);
    });
  }
  const list = readList(c.values, `« ${c.kind} »`);
  const has = (v) => row.full.some((w) => eq(w, v));
  if (c.kind === 'reached') return list.every(has);
  if (c.kind === 'avoids') return !list.some(has);
  if (c.kind === 'within') return row.full.every((w) => list.some((v) => eq(v, w)));
  if (c.kind === 'set') return list.every(has) && row.full.every((w) => list.some((v) => eq(v, w)));
  throw new TablesModelError(`genre d’affirmation inconnu : « ${c.kind} »`);
}
/** l'affirmation se lit sur le tableau rempli (les x d'un couple sont dans le tableau) */
function valuesClaimReadable(d, c) {
  if (c.kind !== 'pairs') return true;
  return (c.pairs ?? []).every(([xs]) => {
    const x = readQ(xs);
    return !!x && d.xs.some((y) => eq(x, y));
  });
}
export function createKTabState(model, table) {
  const spec = model.kernel;
  if (spec.plugin !== 'tables' || !spec.tclaims)
    throw new TablesModelError('Tableau à remplir attendu (plugin « tables »).');
  const data = tableData(model, table);
  const start = parseGrid(spec.start);
  const claims = spec.tclaims.map((c) => ({ spec: c, truth: claimTruth(data, c) }));
  const known = new Set();
  // ce que l'énoncé donne déjà
  if (data.kind === 'series') {
    if (data.form !== 'raw') data.values.forEach((v) => known.add(`n(${qText(v)})`));
    if (data.form === 'cumulative') {
      known.add('N');
      data.values.forEach((v) => known.add(`ECC(${qText(v)})`));
    }
  }
  const ks = {
    model,
    data,
    start,
    expected: expectedGrid(data, start),
    claims,
    known,
    propKnown: false,
    table: false,
    decisions: claims.map(() => null),
    single: !!spec.single,
    path: spec.path ?? [],
    lines: 0,
    texts: [],
  };
  ks.decisions = decide(ks);
  return ks;
}
/* ------------------------------ décisions ------------------------------ */
/** la clé canonique d'une grandeur d'affirmation (« n(12,0) » → « n(12) ») */
function canonKey(k) {
  const n = normName(k);
  const m = /^(n|ECC|y)\((.+)\)$/.exec(n);
  if (!m) return n;
  const v = readQ(m[2]);
  return v ? `${m[1]}(${qText(v)})` : n;
}
function decideOne(ks, c) {
  const d = ks.data;
  // tableau des fréquences (lot 6.23) : tout se lit sur le tableau rempli
  if (d.kind === 'freq') return c.spec.kind !== 'other' && ks.table ? c.truth : null;
  const stats = Object.entries(c.spec.stats ?? {});
  const statDecision = () => {
    let all = true;
    for (const [k, v] of stats) {
      const key = canonKey(k);
      if (!ks.known.has(key)) {
        all = false;
        continue;
      }
      const want = statValue(d, key);
      const got = readQ(v);
      if (!want || !got || !eq(want, got)) return false;
    }
    return all ? true : null;
  };
  if (c.spec.kind === 'value') return statDecision();
  if (c.spec.kind === 'not_proportional')
    return ks.propKnown ? d.kind === 'proportion' && !d.proportional : null;
  if (c.spec.kind === 'proportional') {
    if (!ks.propKnown) return null;
    if (d.kind !== 'proportion' || !d.proportional) return false;
    return statDecision();
  }
  // tableau de valeurs (lot 6.22) : tout se lit sur le tableau rempli
  if (d.kind === 'values' && c.spec.kind !== 'other') {
    if (!ks.table || !valuesClaimReadable(d, c.spec)) return null;
    return c.truth;
  }
  return null;
}
function decide(ks) {
  const d = ks.claims.map((c) => decideOne(ks, c));
  if (ks.single) {
    const trues = d.filter((v) => v === true).length;
    if (trues === 1) return d.map((v) => v === true);
    const open = d.filter((v) => v === null).length;
    if (!trues && open === 1) return d.map((v) => v === null);
  }
  return d;
}
/* ------------------------------ jugement ------------------------------ */
function fillRule(ks) {
  if (ks.data.kind === 'values' && ks.data.mod !== null) return RESIDUE_RULE;
  return FILL_RULE[ks.data.kind];
}
function judgeGrid(ks, text) {
  const A = [text];
  const t = parseGrid(text);
  if (!t)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Tableau illisible',
      message: 'Je ne lis pas ce tableau : ouvre-le de nouveau depuis la palette.',
      badChip: 0,
    };
  if (ks.table && ks.texts.includes(text))
    return {
      kind: 'dup',
      atoms: A,
      title: 'Déjà rempli',
      message: 'Ce tableau est déjà sur ta feuille : lis-le pour trancher les choix.',
    };
  const issue = checkGrid(ks.expected, t);
  if (issue)
    return {
      kind: 'false',
      atoms: A,
      title: issue.title,
      message: issue.message,
      badChip: 0,
      ...(issue.row !== undefined
        ? { cell: { row: issue.row, ...(issue.col !== undefined ? { col: issue.col } : {}) } }
        : {}),
    };
  const after = afterTable(ks);
  return {
    kind: 'step',
    atoms: A,
    hora: fillRule(ks).hora,
    rule: fillRule(ks),
    grid: true,
    decided: newlyDecided(ks.decisions, after.decisions),
    final: after.decisions.every((x) => x !== null),
  };
}
/** ce que le tableau juste établit */
function tableFacts(ks) {
  const d = ks.data;
  const out = [];
  if (d.kind === 'series') {
    out.push('N');
    d.values.forEach((v) => out.push(`n(${qText(v)})`, `ECC(${qText(v)})`));
    return { known: out, prop: false };
  }
  if (d.kind === 'proportion') {
    d.a.forEach((x) => out.push(`y(${qText(x)})`));
    if (d.coef && d.proportional) out.push('k');
    return { known: out, prop: d.coef };
  }
  if (d.kind === 'values' || d.kind === 'freq') return { known: out, prop: false };
  d.labels.forEach((l) => out.push(`T(${l.trim()})`));
  d.head.forEach((l) => out.push(`T(${l.trim()})`));
  out.push('T');
  return { known: out, prop: false };
}
function afterTable(ks) {
  const f = tableFacts(ks);
  const known = new Set([...ks.known, ...f.known]);
  const tmp = { ...ks, known, propKnown: ks.propKnown || f.prop, table: true };
  return { known, decisions: decide(tmp) };
}
/** « Me = 9 » → { name, value } ; null si la ligne n'a pas cette forme */
function readLine(A) {
  if (A.length < 3 || A[1] !== '=') return null;
  const value = A.slice(2).join(' ').trim();
  if (!value) return null;
  return { name: normName(A[0]), value };
}
/** le tableau (ou l'énoncé) donne-t-il de quoi lire cette grandeur ? */
function readable(ks, name) {
  const d = ks.data;
  if (d.kind === 'series') {
    if (name === 'N') return ks.table || d.form !== 'raw';
    return ks.table || d.form === 'cumulative';
  }
  if (d.kind === 'proportion') return ks.table;
  return false;
}
function readMessage(ks, name, got) {
  const d = ks.data;
  if (d.kind === 'series') {
    const N = d.N.toString();
    if (name === 'N') return 'N est l’effectif total : la somme des effectifs.';
    if (name === 'Me')
      return d.N % 2n === 1n
        ? `Me ≠ ${got} : N = ${N} est impair, la médiane est la valeur de rang (N + 1) ÷ 2 = ${(d.N + 1n) / 2n} dans la série rangée.`
        : `Me ≠ ${got} : N = ${N} est pair, la médiane est la moyenne des valeurs de rangs ${d.N / 2n} et ${d.N / 2n + 1n} dans la série rangée.`;
    const quarter = name === 'Q₁' ? 1n : 3n;
    const rank = ceilDiv(quarter * d.N, 4n);
    const frac = qText(div(q(quarter * d.N), q(4)));
    return `${name} ≠ ${got} : c’est la première valeur dont l’effectif cumulé atteint ${quarter === 1n ? 'N ÷ 4' : '3N ÷ 4'} = ${frac} (rang ${rank}).`;
  }
  if (d.kind === 'proportion' && !d.proportional)
    return 'Les quotients ne sont pas tous égaux : le tableau n’est pas proportionnel, il n’a pas de coefficient.';
  return `k ≠ ${got} : k est le quotient (2e ligne) ÷ (1re ligne), le même dans chaque colonne.`;
}
function judgeRead(ks, A, opts) {
  const r = readLine(A);
  const names = READABLE[ks.data.kind];
  if (!r) {
    // une ligne en cours (« Me », « Me = »)
    if (A.length <= 2 && names.includes(normName(A[0])) && (A.length === 1 || A[1] === '='))
      return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Lis le tableau',
      message: names.length
        ? `Écris une grandeur lue dans le tableau : « ${names[names.length - 1]} = … ».`
        : 'Ici, tout se lit dans le tableau : remplis-le (bouton « Tableau » de la palette).',
      badChip: 0,
    };
  }
  if (!names.includes(r.name))
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Pas dans ce tableau',
      message: `Ici, on lit ${names.map((n) => `« ${n} »`).join(', ')}.`,
      badChip: 0,
    };
  const got = readQ(r.value);
  if (!got)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Valeur illisible',
      message: 'Écris un nombre après « = » : « 12 », « 8,5 » ou « 3/4 ».',
      badChip: A.length - 1,
    };
  if (ks.known.has(r.name) && ks.texts.some((t) => readLine(t.split(' '))?.name === r.name))
    return {
      kind: 'dup',
      atoms: A,
      complete: true,
      title: 'Déjà écrit',
      message: `${r.name} est déjà sur ta feuille.`,
    };
  const want = statValue(ks.data, r.name);
  if (!want || !eq(want, got)) {
    if (!opts?.verify) return { kind: 'pending', atoms: A, complete: true };
    return {
      kind: 'false',
      atoms: A,
      complete: true,
      title: 'Pas la bonne valeur',
      message: readMessage(ks, r.name, r.value),
      badChip: A.length - 1,
    };
  }
  // déjà établie par le tableau (« N » : le dernier effectif cumulé)
  if (ks.known.has(r.name))
    return {
      kind: 'useless',
      atoms: A,
      complete: true,
      title: 'Déjà dans ton tableau',
      message: `${r.name} se lit déjà dans ton tableau : passe à la suite.`,
    };
  const known = new Set([...ks.known, r.name]);
  const decisions = decide({ ...ks, known });
  const final = decisions.every((x) => x !== null);
  if (!readable(ks, r.name))
    return {
      kind: 'useless',
      atoms: A,
      complete: true,
      title: final ? 'Tu tiens la réponse !' : 'Juste !',
      message:
        ks.data.kind === 'series'
          ? 'Juste, mais montre d’où elle vient : remplis le tableau des effectifs cumulés (bouton « Tableau » de la palette).'
          : 'Juste, mais montre d’où elle vient : remplis le tableau (bouton « Tableau » de la palette).',
      read: { name: r.name },
      jump: true,
      final,
    };
  return {
    kind: 'step',
    atoms: A,
    complete: true,
    hora: READ_RULE.hora,
    rule: READ_RULE,
    read: { name: r.name },
    decided: newlyDecided(ks.decisions, decisions),
    final,
  };
}
export function ktabJudge(ks, chips, opts) {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A };
  const grids = A.filter(isGridText);
  if (grids.length) {
    if (A.length !== 1)
      return {
        kind: 'syntax',
        atoms: A,
        title: 'Le tableau seul sur sa ligne',
        message: 'Un tableau occupe toute une ligne : efface ce qu’il y a autour.',
        badChip: A.findIndex((a) => !isGridText(a)),
      };
    return judgeGrid(ks, A[0]);
  }
  return judgeRead(ks, A, opts);
}
export function ktabCommit(ks, v) {
  if (v.grid) {
    const f = tableFacts(ks);
    f.known.forEach((k) => ks.known.add(k));
    if (f.prop) ks.propKnown = true;
    ks.table = true;
    ks.texts.push(v.atoms[0]);
  } else {
    if (v.read) ks.known.add(v.read.name);
    ks.texts.push(v.atoms.join(' '));
  }
  ks.decisions = decide(ks);
  ks.lines++;
}
export function ktabDecisions(ks) {
  return ks.decisions;
}
export function ktabGoalReached(ks) {
  return ks.decisions.every((d) => d !== null);
}
/** la ligne est vraie (le tableau juste ; une grandeur bien lue) */
export function ktabHolds(ks, text) {
  if (isGridText(text)) {
    const t = parseGrid(text);
    return !!t && !checkGrid(ks.expected, t);
  }
  const r = readLine(ktabLineAtoms(text));
  if (!r) return false;
  const want = statValue(ks.data, r.name);
  const got = readQ(r.value);
  return !!want && !!got && eq(want, got);
}
/** une ligne déjà sur la feuille */
export function ktabEstablished(ks, text) {
  if (isGridText(text)) return ks.table && ks.texts.includes(text);
  const r = readLine(ktabLineAtoms(text));
  return !!r && ks.known.has(r.name) && ktabHolds(ks, text);
}
export function ktabLineAtoms(text) {
  if (isGridText(text)) return [text];
  return text.trim().split(/\s+/);
}

// ── Interface avec le juge du moteur v2 (judge.js) ─────────────────────────
/** copie de l'état (le juge v2 ne modifie jamais une feuille en place) */
function cloneKTab(ks) {
  return {
    ...ks,
    known: new Set(ks.known),
    decisions: [...ks.decisions],
    texts: [...ks.texts],
  };
}

/** état d'une feuille « tableau » (lève TablesModelError si le modèle ne va pas) */
export function createTableSheet(model, table) {
  return createKTabState(model, table);
}

/** relit le texte du tableau de l'énoncé (la ligne de départ de la feuille) */
export function tableStartText(ks) {
  return gridText(ks.start);
}

/**
 * Verdict du moteur v2 pour une ligne : { status, kind, message, title, gestures, hora, tab } — `tab` est le
 * nouvel état de la feuille quand la ligne est gardée, `decided` les choix qu'elle tranche, `cell` la case fausse.
 */
export function judgeTableLine(ks, text) {
  const v = ktabJudge(ks, [{ atoms: ktabLineAtoms(String(text)) }], { verify: true });
  const base = { title: v.title ?? null, message: v.message ?? null };
  if (v.cell) base.cell = v.cell;
  if (v.kind === 'step' || (v.kind === 'useless' && v.jump)) {
    const next = cloneKTab(ks);
    ktabCommit(next, v);
    const done = ktabGoalReached(next);
    const rule = v.rule;
    const gestures =
      v.kind === 'step' && rule
        ? [{ ruleId: rule.id, label: rule.label, why: rule.why, hora: rule.hora ?? 0 }]
        : [];
    return {
      ...base,
      status: v.kind === 'useless' ? 'useless' : done ? 'goal' : 'step',
      kind: v.kind === 'useless' ? 'kept' : 'step',
      gestures,
      hora: v.kind === 'step' ? (v.hora ?? 0) : 0,
      decided: v.decided ?? newlyDecided(ks.decisions, next.decisions),
      message:
        v.message ?? (done ? 'Tu tiens la réponse !' : rule ? `Juste : ${rule.label}.` : 'Juste.'),
      tab: next,
    };
  }
  if (v.kind === 'dup' || v.kind === 'useless')
    return { ...base, status: 'useless', kind: 'kept', gestures: [], hora: 0 };
  if (v.kind === 'false' || v.kind === 'trap')
    return { ...base, status: v.kind === 'trap' ? 'trap' : 'wrong', kind: null };
  // ligne vide, en cours ou illisible
  return {
    ...base,
    status: 'parse_error',
    kind: null,
    error: { pos: v.badChip ?? 0, len: 1, msg: v.message ?? 'Ligne incomplète.' },
  };
}

/** décisions des choix (true / false / null) d'une feuille « tableau » */
export function tableDecisions(ks) {
  return ktabDecisions(ks);
}
